/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used
 * in more than one place (or representing a product decision, not a local
 * detail) lives here, not as a standalone `const` inside a component.
 *
 * Reason: these values were redeclared in ~12 files with two spellings
 * of the same number (`5 * 60 * 1000` and `300_000`), so that
 * changing the cache policy required touching all those places and risked
 * leaving divergent values. Import from here instead of redefining them.
 /** Waits for the app to finish booting (skeleton overlay disappears). */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

/** Milliseconds in one minute. Base for deriving the remaining durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Milliseconds in one hour. Used by the foreground reminders window. */
export const ONE_HOUR_MS = MINUTES_PER_HOUR * MINUTE_MS;

/** Minutes for which cached data is considered fresh (offline and session caches). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data is synchronized locally through PowerSync, so a generous
 * (5 min) avoids redundant refetches/re-renders without any real risk of
 * show stale data. It is also the global `queryClient` default.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It remains a separate constant (although it currently matches the
 * offline-cache value) because it is an independent decision: it must stay aligned
 * across `useSession`, the `_app` route guard, and `session-bridge`.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Time cap for the session check performed by the route guards
 * (`_app/route.tsx` and `_auth/route.tsx`) in their `beforeLoad`.
 *
 * P23/CC-60 · I1-bis. This defect does NOT occur with either a 401 or a 503
 * (both resolve quickly, and the `queryFn` is also `result.data ?? null` over
 * openapi-fetch, so a 5xx resolves in SUCCESS with `null`): it fails when the
 * response STALLS. Without a cap, `beforeLoad` does not resolve, the router does not mount the
 * route, the `<Suspense fallback={null}>` in `__root` has nothing to render,
 * and the entire app stays BLANK for as long as the network takes. Measured on
 * `/login` in this repo: `document.body.innerText` EMPTY at 3, 6, 10, 15, and
 * 20 s, with the request still stalled.
 *
 * SINGLE source for both guards: having the number in two local literals is
 * exactly what left `_auth` without the defense that `_app` had, and what
 * caused I1 to close green by measuring `/` (healthy) instead of
 * `/login` (broken). Once the cap is reached, the screen renders; the late response
 * remains alive and synchronizes state + cache, and `BootManager` redirects afterward
 * if a session turned out to exist: the cost is a flicker, never a blank screen.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/**
 * Debounce for text inputs that persist while typing (detail title and notes,
 * subtasks). Balances typing fluidity against the number of mutations.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export const DEBOUNCE_DELAY_MS = 400;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after triggering a download.
 *
 * It must be comfortably greater than the time the browser takes to consume the href
 * of `<a download>`; revoking it earlier would abort the download.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
